/**
 * I18nProvider — internationalisation context provider.
 *
 * Behaviour:
 *   - When `i18n-enabled` feature flag is OFF (production default):
 *     Mounts with the inlined en-US catalogue synchronously. No async import.
 *   - When flag is ON AND locale is en-US:
 *     Same synchronous path — inlined en-US catalogue.
 *   - When flag is ON AND locale differs (e.g. es-CL):
 *     Dynamic-imports `./locales/{locale}.json`. While loading, falls back to
 *     en-US. On import error, falls back to en-US permanently for the session.
 *
 * Mounted in AppShell immediately inside <ConvexErrorBoundary>.
 *
 * Source contract: specs/prd.md FR-023.
 */
import React, { createContext, useEffect, useMemo, useState } from "react";
import { setLocale } from "./getLocale";
import enUS from "./locales/en-US.json";

/* ------------------------------------------------------------------ */
/*  Context shape                                                      */
/* ------------------------------------------------------------------ */

export interface I18nContextValue {
  /** Active locale catalogue (may be a non-English locale or en-US). */
  catalogue: Record<string, string>;
  /** English fallback catalogue (always loaded). */
  fallback: Record<string, string>;
  /** Active BCP 47 locale tag. */
  locale: string;
}

export const I18nContext = createContext<I18nContextValue>({
  catalogue: enUS as Record<string, string>,
  fallback: enUS as Record<string, string>,
  locale: "en-US",
});

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

interface I18nProviderProps {
  children: React.ReactNode;
  /** Whether the i18n feature flag is enabled. Default false. */
  enabled?: boolean;
  /**
   * The user's preferred locale from user settings (e.g. "es-CL").
   * When undefined or "en-US", the provider uses the inlined en-US catalogue.
   */
  userLocale?: string;
}

/**
 * I18nProvider wraps the application tree with translation context.
 *
 * When the feature flag is off or the locale is en-US, this is a zero-cost
 * passthrough — no async imports, no loading state, no bundle impact.
 */
export function I18nProvider({
  children,
  enabled = false,
  userLocale,
}: I18nProviderProps): React.ReactElement {
  const fallback = enUS as Record<string, string>;
  const resolvedLocale =
    enabled && userLocale && userLocale !== "en-US" ? userLocale : "en-US";

  const [catalogue, setCatalogue] = useState<Record<string, string>>(fallback);

  // Update the module-level locale reference for formatter utilities.
  useEffect(() => {
    setLocale(resolvedLocale);
  }, [resolvedLocale]);

  // Dynamic-import the locale catalogue when needed.
  useEffect(() => {
    if (resolvedLocale === "en-US") {
      setCatalogue(fallback);
      return;
    }

    let cancelled = false;

    import(`./locales/${resolvedLocale}.json`)
      .then((mod: { default: Record<string, string> }) => {
        if (!cancelled) {
          setCatalogue(mod.default);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          if (import.meta.env.DEV) {
            console.warn(
              JSON.stringify({
                event: "i18n.locale_import_failed",
                locale: resolvedLocale,
                error: String(err),
              }),
            );
          }
          // Fall back to en-US on import error.
          setCatalogue(fallback);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedLocale, fallback]);

  const value = useMemo<I18nContextValue>(
    () => ({ catalogue, fallback, locale: resolvedLocale }),
    [catalogue, fallback, resolvedLocale],
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
}
